import React, { useRef, useState } from "react";

import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import logo from "../../assets/SEICLogoDark.svg";
import logoLight from "../../assets/SEICLogoLight.svg";
import LoginBackdropDark from "../../assets/login_graphic_dark.svg";
import LoginBackdropLight from "../../assets/login_graphic_light.svg";
// import { useAuthenticateMutation } from '../../services/portal/authenticateApi';

import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { authenticate, SET_NAV } from "../../actions";
import LoadingButton from "../../components/LoadingButton";
import { seicDark } from "../../shared-components/src/gnb-theme-ts";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";

const { useEffect } = React;

type Section = "login" | "forgot-password" | "reset-password";

const parseQuery = (queryString: string) => {
  const query: { [key: string]: string } = {};
  const pairs = (queryString[0] === "?"
    ? queryString.substr(1)
    : queryString
  ).split("&");
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i].split("=");
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || "");
  }
  return query;
};

const SignIn: React.FC = () => {
  // const [authenticate] = useAuthenticateMutation();
  const nav = useSelector((state) => state.nav);
  const dispatch = useDispatch();
  const { loading: isLoading, auth_token, userInfo } = useSelector(
    (state) => state.authenticate
  );

  const isLoggedIn = auth_token && userInfo?.exp > Date.now() / 1000;
  useEffect(() => {
    if (isLoggedIn) {
      dispatch({
        type: SET_NAV,
        payload: { index: "dashboard", drawerOpen: false },
      });
    }
  }, []);

  const searchParams = parseQuery(nav.search || "");

  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // This is a hack to make sure that the resetParams are not reset on every render
  const resetParams = useRef({ email: "", token: "", type: "" });
  resetParams.current = {
    token: resetParams.current.token || searchParams.token,
    email: resetParams.current.email || searchParams.email,
    type: resetParams.current.type || searchParams.type,
  };

  const [section, setSection] = React.useState<Section>(
    searchParams.token ? "reset-password" : "login"
  );
  const [prevSection, setPrevSection] = React.useState<Section>("login");
  const [errors, setErrors] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { breakpoints, palette } = useTheme();
  const small = useMediaQuery(breakpoints.down("lg"));

  useEffect(() => {
    setTimeout(() => {
      setPrevSection(section);
      // prettier-ignore
      const input = (document.querySelector(".forgot-password-email input") as HTMLInputElement)
      if (section === "forgot-password" && input) {
        input.focus();
        input.select();
      }
    }, 400);
  }, [section]);

  const validate = () => {
    const _errors: string[] = [];
    if (!email) {
      _errors.push("Email required.");
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
      _errors.push("Invalid email address.");
    }
    if (!password) {
      _errors.push("Password required.");
    }
    setErrors(_errors);
    return _errors.length === 0;
  };

  const handleSignIn = async () => {
    if (!validate()) {
      return;
    }
    try {
      dispatch(authenticate({ email: email, password }));
    } catch (e) {
      console.log(e);
      return {
        type: "CredentialsSignin",
        error: "Invalid credentials.",
      };
    }
  };

  if (isLoggedIn) return null;

  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      width={"100%"}
      height={"100vh"}
      spacing={"80px"}
    >
      <Grid
        item
        xs={0}
        lg={7}
        justifyContent="center"
        sx={{ display: small ? "none" : "flex", justifyContent: "flex-end" }}
      >
        <Box
          sx={{
            backgroundImage: `url(${
              palette.mode == "dark" ? LoginBackdropDark : LoginBackdropLight
            })`,
            backgroundSize: "100%",
            height: "100vh",
            maxWidth: "650px",
            zIndex: 1,
            width: "100%",
            backgroundRepeat: "no-repeat",
            WebkitBackgroundPosition: "center",
            backgroundPosition: "center",
          }}
        ></Box>
      </Grid>
      <Grid
        item
        data-testid="abcd"
        size={{ lg: 5, xs: 12 }}
        xs={12}
        lg={5}
        justifyContent="center"
        alignItems={"center"}
        sx={{ display: "flex", justifyContent: "flex-start" }}
      >
        <Box
          sx={{
            display: "grid",
            overflow: "hidden",
            margin: { xs: "0 auto", lg: "inherit" },
          }}
          width={344}
        >
          <Box
            component="img"
            src={palette.mode == "dark" ? logo : logoLight}
            alt="logo"
            sx={{ mb: "48px", display: "flex" }}
          />
          <Box
            sx={{
              display: "flex",
              flex: 1,
              gap: 1,
              width: "200%",
              translate: section !== "login" ? "-50%" : "0",
              transition: "translate 0.2s",
              flexWrap: "wrap",
            }}
          >
            <Box flex={1}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSignIn();
                }}
              >
                <Typography
                  variant="h2"
                  sx={{ fontSize: 32, fontWeight: 400, mb: "24px" }}
                >
                  Log In
                </Typography>
                <TextField
                  label="Email"
                  name="email"
                  value={email}
                  autoFocus
                  variant="filled"
                  onChange={(e) => {
                    setShowPassword(false);
                    setEmail(e.target.value);
                  }}
                  fullWidth
                  sx={{ mb: "16px" }}
                />
                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  variant="filled"
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          className="end"
                          sx={{
                            opacity: password ? 1 : 0,
                            color: "text.tertiary",
                          }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <VisibilityOffOutlined />
                          ) : (
                            <VisibilityOutlined />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: "24px",
                  }}
                />
                {!!errors.length && (
                  <Box
                    sx={{
                      mb: "24px",
                      textAlign: "left",
                      border: "solid 1px",
                      borderColor: "error.main",
                      borderRadius: "4px",
                      p: "8px 16px",
                      lineHeight: "16px",
                      "& a:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {errors.map((err) => (
                      <Typography
                        key={err as string}
                        variant="body1"
                        sx={{
                          fontSize: 12,
                          color: "error.main",
                          fontWeight: 400,
                          lineHeight: "16px",
                        }}
                      >
                        {err as string}
                      </Typography>
                    ))}
                  </Box>
                )}
                <LoadingButton
                  size="large"
                  loading={isLoading}
                  variant="contained"
                  fullWidth
                  type="submit"
                  sx={{ mb: "8px" }}
                >
                  Log in{" "}
                </LoadingButton>
              </form>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <FormControlLabel
                  control={<Switch size="small" />}
                  onChange={(e) => setRememberMe(!rememberMe)}
                  value={rememberMe}
                  label="Remember Me"
                  sx={{ pl: "7px" }}
                  slotProps={{
                    typography: {
                      variant: "caption",
                      color: rememberMe ? "text" : "text.secondary",
                      sx: {
                        fontSize: 14,
                        fontWeight: 400,
                        "&:hover": { color: "text.primary" },
                      },
                    },
                  }}
                />
                <Box
                  color="inherit"
                  onClick={() => setSection("forgot-password")}
                  sx={{
                    fontSize: 14,
                    fontWeight: 400,
                    cursor: "pointer",
                    color: "text.secondary",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Forgot Password?
                </Box>
              </Box>
            </Box>
            <Box flex={1}>
              {[section, prevSection].includes("forgot-password") && (
                <ForgotPassword
                  signInEmail={email ?? ""}
                  onBack={() => setSection("login")}
                />
              )}
              {[section, prevSection].includes("reset-password") && (
                <ResetPassword
                  onBack={() => setSection("login")}
                  token={resetParams.current.token}
                  email={resetParams.current.email}
                  type={resetParams.current.type}
                />
              )}
            </Box>
          </Box>
          <Box
            sx={{
              mb: "24px",
              textAlign: "center",
              mt: "86px",
              "& a:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <Typography
              color={seicDark.textLowEmphasis}
              variant="caption"
              sx={{ fontSize: 14, fontWeight: 400 }}
            >
              Not a customer yet?{" "}
            </Typography>
            <a
              size="small"
              target="_blank"
              style={{
                fontSize: 14,
                fontWeight: 400,
                padding: 0,
                textTransform: "none",
                color: seicDark.primary,
              }}
              href="https://www.stryten.com/contact/"
            >
              Contact us
            </a>
          </Box>
          <Typography
            sx={{ fontSize: 14, fontWeight: 400, textAlign: "center" }}
            color={seicDark.textLowEmphasis}
          >
            inCOMMAND is a registered trademark of Stryten Energy LLC.
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default SignIn;
